import { useQuery } from '@tanstack/react-query';
import { Globe, Network, Router, ShieldCheck, Wifi } from 'lucide-react';
import type { ObservedWanIpv6 } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { ipv6TitlePill } from '../../lib/wan-ipv6';
import { Card, Pill, Spinner } from '../../components/ui';
import { RefreshButton } from '../../components/RefreshButton';

/**
 * What this ONU is, in four lines.
 *
 * A summary, not a dump: the WAN, the address we manage it on, what it serves
 * the LAN, and which SSIDs are up. Anything deeper belongs in the cards below,
 * which is why those are collapsed until somebody wants one.
 *
 * Read from the ONU rather than from what SimpleACS meant to configure. The two
 * are usually the same, and the times they are not are exactly when somebody is
 * looking at this card.
 *
 * Management and WAN are told apart deliberately. This platform reaches a CPE
 * only over the management connection — `iphost 1`, configured before anything
 * else — so an ONU with nothing but management reports the same address twice
 * unless the WAN row knows to exclude it. It did, and both rows read 10.10.1.32
 * as though the subscriber were online on the management network.
 */
interface OmciWan {
  /** management = the connection the platform manages the ONU over; never the subscriber's WAN. */
  role?: 'management' | 'subscriber' | 'unknown';
  mode: string | null;
  cvlan: number | null;
  ip: string | null;
  mask: string | null;
  gateway: string | null;
  dns1: string | null;
  dns2: string | null;
  pingResponse: boolean | null;
}
interface OmciIpHost {
  hostId: number;
  ip: string | null;
  mask: string | null;
  gateway: string | null;
  dns: string[];
  dhcp: boolean;
  pingResponse: boolean;
}
interface OmciDhcpServer {
  enabled: boolean;
  poolStart: string | null;
  gateway: string | null;
}
interface OmciSsid {
  index: number;
  name: string | null;
  hasKey: boolean;
  /** What the chassis reports even when the name is not its to know. */
  hidden?: boolean;
  currentUsers?: number | null;
  auth?: string | null;
}
/** `show gpon remote-onu wifi info` — the radio itself, ZTE only. */
interface OmciRadio {
  enabled?: boolean;
  operational?: boolean;
  workMode?: string | null;
  channel?: number | null;
}
interface HuaweiServicePort {
  index: number;
  vlanId: number;
  gemIndex: number | null;
  state: string | null;
}
/** ZTE: which VLAN the ONU is managed on, from the chassis. */
interface OmciTr069 {
  vlanId: number | null;
  acsUrl: string | null;
  unlocked: boolean;
}
/** ZTE: the VLANs the OLT carries to this ONU. */
interface OmciServicePort {
  index: number;
  vport: number;
  vlan: number;
}
interface Tr069Device {
  /** Firewall as the CPE reports it over TR-069; null when the model has none. */
  firewall?: boolean | null;
  wan: {
    mode: string | null;
    vlanId: number | string | null;
    externalIp: string | null;
    gateway: string | null;
    pppoeUsername: string | null;
    /** The IPv6 half of the scored subscriber WAN as the CPE reports it (null: no subscriber WAN). */
    ipv6?: ObservedWanIpv6 | null;
  };
  /** The connection this platform reaches the CPE on, which is not the WAN. */
  management: { ip: string | null; vlanId: string | null } | null;
  mgmtIp: string | null;
  lan: {
    ip: string | null;
    subnetMask: string | null;
    dhcpEnabled: boolean | null;
    dns: string | null;
  } | null;
  wlans: Array<{ ssid: string | null; enabled: boolean | null; band: string | null }>;
}
interface Config {
  wan?: OmciWan | null;
  /**
   * The firewall the platform recorded for this ONU (the chassis reports none):
   * true = firewalled, false = open, null/absent = never set. This is the real
   * source for the firewall pill — ping-response is a different signal.
   */
  firewalled?: boolean | null;
  ipHosts?: OmciIpHost[];
  dhcpServer?: OmciDhcpServer | null;
  ssids?: OmciSsid[];
  radio?: OmciRadio | null;
  servicePorts?: HuaweiServicePort[] | OmciServicePort[];
  tr069?: OmciTr069 | null;
  /**
   * The `pon-onu-mng` section as the chassis prints it, parsed.
   *
   * This is where bridged-versus-routed is actually ANSWERED — a VEIP bind or a
   * socket the ONU serves DHCP to on one side, a socket carrying the subscriber
   * VLAN out on the other. Null on a C650, which keeps no per-interface running
   * config; that is a question the chassis does not answer, not a unit with no
   * VEIP.
   */
  running?: {
    ports?: Array<{ port: string; mode: string; pvid: number | null; taggedVlans: number[] }>;
    routedPorts?: Array<{ port: string; routed: boolean }>;
    veipBinds?: number[];
    ipHostVlans?: Array<{ hostId: number; priority: number; vlanId: number }>;
  } | null;
  /** The chassis has not been read for this ONU yet; fields are blank, not absent. */
  unread?: boolean;
}

/** VLANs arrive from the ACS as strings and from the chassis as numbers. */
function numberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** CPEs report their DNS servers as one comma- or space-separated field. */
function splitDns(value: string | null | undefined): string[] | null {
  if (!value) return null;
  const parts = value
    .split(/[,;\s]+/)
    .map((v) => v.trim())
    .filter((v) => v && v !== '0.0.0.0');
  return parts.length > 0 ? parts : null;
}

export function SettingsCard({
  oltId,
  onuId,
  vendor,
  deviceId,
  onEdit,
}: {
  oltId: string;
  onuId: string;
  vendor: string;
  deviceId?: string | null;
  /** Jump to the Configs tab, opening the editor for one setting. */
  onEdit?: (focus: 'wan' | 'lan' | 'wifi' | 'security') => void;
}) {
  const device = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => api.get<Tr069Device>(`/devices/${encodeURIComponent(deviceId!)}`),
    enabled: Boolean(deviceId),
  });
  const omci = useQuery({
    queryKey: ['onu', onuId, 'omci-config'],
    queryFn: () =>
      api.get<Config>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-config`,
      ),
  });

  const config = omci.data;
  const acs = device.data ?? null;
  const huawei = vendor === 'huawei';

  /**
   * Both VLANs come from the chassis, which is the only side that knows them.
   *
   * They were taken from the ACS's view of the ONU's WAN, and on an ONU whose
   * only connection is management that reports the management VLAN — so the WAN
   * row read "VLAN 10" for a subscriber on VLAN 5, and the management row,
   * which had nothing else to draw on, was blank. The chassis says both plainly:
   * `tr069-mgmt` carries the management tag, and the interface's own
   * service-ports are the VLANs it actually carries.
   */
  const huaweiPorts = (config?.servicePorts ?? []) as HuaweiServicePort[];
  const ztePorts = (config?.servicePorts ?? []) as OmciServicePort[];

  const acsMgmtVlan = numberOrNull(acs?.management?.vlanId);

  /**
   * Management is `ip-host 2`. Not `ipHosts[0]`.
   *
   * The slot is the meaning, and the platform is deliberate about it —
   * `planIpHosts` in services/devices/onu-service-config.ts hands the WAN host
   * 1 and management host 2 "whether or not both exist — leaving a gap rather
   * than sliding everything up, so `ip-host 2` means the same thing on every
   * ONU in the fleet". Reading position 0 therefore read the WAN and labelled
   * it Management.
   *
   * Reported by the operator on a real subscriber: the page showed
   * "Management 10.2.0.57 · VLAN 10 · DHCP" next to "WAN · VLAN 5 · address not
   * reported", and 10.2.0.57 was the DHCP address on VLAN 5 — the WAN's. So the
   * one row an engineer uses to reach an ONU was showing an address that does
   * not reach it, under the wrong VLAN, with the WAN's own row blank.
   *
   * This is the standing rule in another costume: the CPE says which connection
   * is which, and picking by position is picking by "the first one that has an
   * address".
   */
  const mgmtHost = config?.ipHosts?.find((h) => h.hostId === 2) ?? null;
  const mgmtIp = mgmtHost?.ip ?? acs?.management?.ip ?? acs?.mgmtIp ?? null;
  const mgmtVlan = huawei
    ? (huaweiPorts.find((sp) => sp.gemIndex === 2)?.vlanId ?? acsMgmtVlan)
    : (config?.tr069?.vlanId ?? ztePorts.find((sp) => sp.vport === 2)?.vlan ?? acsMgmtVlan);

  /**
   * The chassis wan-ip object, but only when it is NOT the management
   * connection. The API names each wan-ip by the VLAN the ONU's own
   * tr069-mgmt line tags; a fleet migrated from SmartOLT carries ONLY that
   * management object on the chassis (Triumph Merchants, Lagos C650,
   * 2026-09-18: "the same vlan for mngt and wan"), and letting it stand in
   * for the subscriber's WAN printed "VLAN 11 · DHCP" under WAN beside the
   * same VLAN 11 under Management. The subscriber's PPPoE WAN lives on the
   * ONU and is only knowable once it informs the ACS.
   */
  const subscriberWan = config?.wan && config.wan.role !== 'management' ? config.wan : null;
  const chassisCarriesOnlyManagement = config?.wan?.role === 'management';

  // The subscriber's, which is every VLAN that is not the management one.
  const wanVlan =
    (huawei
      ? (huaweiPorts.find((sp) => sp.gemIndex !== 2)?.vlanId ?? null)
      : (ztePorts.find((sp) => sp.vlan !== mgmtVlan)?.vlan ?? null)) ??
    numberOrNull(acs?.wan.vlanId) ??
    subscriberWan?.cvlan ??
    null;

  /**
   * And the address, which only the ONU can answer.
   *
   * The chassis does not know a subscriber's WAN address — asking a C300 for
   * one returns nothing even on a working subscriber — so this is a TR-069
   * reading or it is nothing.
   */
  // Host 1 is the WAN, by the same convention that makes host 2 management.
  // Preferred over the ACS reading because it is what the CHASSIS sees, and it
  // is present on a subscriber whose TR-069 has not come up yet.
  const wanHost = config?.ipHosts?.find((h) => h.hostId === 1) ?? null;
  const rawWanIp = wanHost?.ip ?? acs?.wan.externalIp ?? subscriberWan?.ip ?? null;
  /**
   * Two addresses are not the subscriber's WAN. `0.0.0.0` is the chassis WAN
   * host reporting no DHCP lease yet (a routed ONU whose subscriber connection
   * is down or waiting) — `config.wan.ip` returns it literally, and it must read
   * as "no address", not as an IP. And the management address under a WAN
   * heading is the same trap one row up: an ONU whose only connection ACS
   * reports is management answers with that address under every heading.
   */
  const wanIp =
    rawWanIp === null ||
    rawWanIp === mgmtIp ||
    rawWanIp === '0.0.0.0' ||
    rawWanIp === '::' ||
    rawWanIp.trim() === ''
      ? null
      : rawWanIp;
  // The ACS reports modes in lowercase; beside the Management row's "DHCP"
  // tag the mismatch read as two different things.
  const rawWanMode = acs?.wan.mode ?? subscriberWan?.mode ?? null;
  const wanMode =
    // `unknown` is what the CPE says when it has no answer, and printing it as
    // a mode states a fact nobody established.
    rawWanMode === null || /^unknown$/i.test(rawWanMode.trim())
      ? null
      : /dhcp/i.test(rawWanMode)
        ? 'DHCP'
        : /pppoe/i.test(rawWanMode)
          ? 'PPPoE'
          : rawWanMode;

  /**
   * Whether the ONU TERMINATES its subscriber VLAN, asked of the chassis.
   *
   * A bridged unit hands the VLAN straight out and the router behind it does
   * DHCP or PPPoE, so the ONU has no WAN of its own: one IP host, which is
   * management. A routed one has a second host and an address.
   *
   * Asked of the CHASSIS and not of the ACS, because the ACS conflates the two.
   * `HWTC7E1CCE5D` is bridged on VLAN 29 with `ip-host 2` and nothing else, and
   * the ACS reported its WAN as DHCP at 10.10.1.45 — the management address,
   * under the WAN heading. Believing that printed a subscriber connection this
   * ONU has not got, with the wrong address on it.
   */
  const chassisWanIp = subscriberWan?.ip ?? null;
  /**
   * A WAN address that is ALSO the management address is not a subscriber WAN.
   *
   * The OMCI read answers the WAN question with whatever IP connection the ONU
   * has, and on a bridged unit the only one is management — so `wan.ip` comes
   * back as 10.10.1.66 on a subscriber whose management address is 10.10.1.66.
   * Testing for the mere PRESENCE of a WAN address therefore called every
   * bridged ONU routed. The two rows have to disagree for the WAN to be real.
   */
  const chassisHasOwnWan =
    (chassisWanIp !== null && chassisWanIp !== mgmtIp) || (config?.ipHosts?.length ?? 0) > 1;

  /**
   * Bridged or routed, from what the chassis SAYS rather than from what it
   * omits.
   *
   * This used to be `wanVlan != null && !chassisHasOwnWan` — absence of a
   * second IP host taken as proof of bridging. It called every routed
   * subscriber on gachie1 bridged, because the repair that fixed their
   * management path cleared the unused `ip-host 1`: a routed F670L there shows
   * ONE host and terminates its subscriber VLAN on its own routing stack, which
   * an IP-host count cannot see. Two live examples, both drawn as bridged and
   * both routers: ZXICCD5A2B90 at 1/2/1:12 and RTEGC6A2955E at 1/3/15:7.
   *
   * So each direction now needs its own positive evidence:
   *
   *   ROUTED — `switchport-bind switch_0/1 veip 1`, the ONU's own routing stack
   *   bound to the switch, or LAN sockets it answers DHCP for
   *   (`dhcp-ip ethuni eth_0/N from-onu`). Either says the unit terminates
   *   traffic itself.
   *
   *   BRIDGED — the subscriber VLAN handed OUT of a socket
   *   (`vlan port eth_0/3 mode hybrid def-vlan 5`). That is what this platform
   *   renders for a bridged LAN port, and what SmartOLT renders too.
   *
   * With neither, the answer is UNKNOWN and the row says nothing — the C650
   * keeps no per-interface running config, so a missing `switchport-bind` there
   * is a read this chassis does not answer rather than a unit that has none.
   * Guessing in that gap is exactly what produced the wrong label.
   */
  const running = config?.running ?? null;
  /**
   * Does the ONU terminate the SUBSCRIBER VLAN? Asked of the one line that
   * says exactly that: `vlan-filter iphost N … vlan <wan>` — the WAN VLAN
   * carried on one of the ONU's own IP interfaces.
   *
   * Two older proxies both lied here. `switchport-bind … veip` exists on
   * EVERY TR-069-managed unit — it is the MANAGEMENT plumbing — so a bridged
   * subscriber whose ONU we manage read as routed, wearing "DHCP · address
   * not reported" about a connection the ONU does not terminate
   * (HWTC7E1CCE5D, 2026-08-28). And `dhcp-ip … from-onu` is stored by the
   * chassis even when the ONT DECLINED it (`unknown ME` on third-party
   * units), so it echoes intent, not termination.
   */
  const terminatesItself =
    (running?.ipHostVlans ?? []).some((h) => wanVlan != null && h.vlanId === wanVlan) ||
    chassisHasOwnWan;
  const handsVlanOut =
    wanVlan != null &&
    (running?.ports ?? []).some(
      (p) => p.pvid === wanVlan || (p.taggedVlans ?? []).includes(wanVlan),
    );
  const bridged = wanVlan != null && handsVlanOut && !terminatesItself;
  const routed = wanVlan != null && terminatesItself;

  /**
   * The firewall the platform actually recorded — the chassis reports no
   * firewall state in any read, and the `firewall enable/disable` verb does NOT
   * touch ping-response, so deriving it from ping-response (as this card used to)
   * was simply wrong. Null — never set — leaves the pill absent rather than
   * guessed, the same tri-state the WAN mode honours.
   */
  // Prefer the firewall the CPE itself reports over TR-069 (authoritative once
  // the ONU is reachable); fall back to the OLT-side recorded intent for an
  // OMCI-only ONU. Null on both leaves the pill off — the model has no firewall
  // or we have never set one.
  const firewalled = acs?.firewall ?? config?.firewalled ?? null;

  const lanIp = acs?.lan?.ip ?? config?.dhcpServer?.gateway ?? null;
  const lanDhcp = acs?.lan?.dhcpEnabled ?? config?.dhcpServer?.enabled ?? null;
  // Whatever the ONU hands its own clients, which is not always the gateway.
  const lanDns = splitDns(acs?.lan?.dns) ?? mgmtHost?.dns?.filter(Boolean) ?? [];

  const ssids = acs?.wlans.length
    ? acs.wlans.filter((w) => w.enabled !== false && w.ssid).map((w) => ({ name: w.ssid!, band: w.band }))
    : (config?.ssids ?? []).filter((s) => s.name).map((s) => ({ name: s.name!, band: null }));
  /**
   * What the chassis DOES answer about WiFi when it cannot answer the names.
   *
   * A ZTE reports the radio and every SSID slot over OMCI — auth mode, hidden
   * or not, connected users — but the NAME column is only filled for SSIDs
   * written over OMCI. A router that named its own WiFi (or was named by
   * another ACS) reports blank names, and this card used to read that as
   * "No SSID is up" — asserting, about a working router, the one thing the
   * chassis had not said. Live counts are the useful part: connected users on
   * a slot is proof the WiFi is up regardless of anybody knowing its name.
   */
  const omciRadio = config?.radio ?? null;
  const omciSlots = config?.ssids ?? [];
  const omciWifiUsers = omciSlots.reduce((n, s) => n + (s.currentUsers ?? 0), 0);

  /**
   * The IPv6 half as ONE pill in the title bar (operator, 2026-09-14: "the
   * onu card is too busy … move the ipv6 pill to the title bar") — what the
   * CPE REPORTS on the scored subscriber WAN, never the record; the observed
   * words that used to crowd the WAN row are its tooltip. Absent on a
   * bridged unit (its ACS WAN reading is the management connection's) and
   * where the CPE reads IPv4-only, unknown, or has no IPv6 leaf.
   */
  const ipv6Pill = bridged ? null : ipv6TitlePill(acs?.wan.ipv6);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Router className="size-4 text-muted" />
          ONU
        </span>
      }
      action={
        <div className="flex items-center justify-end gap-1.5">
          {/* Routed | Bridged, IPv6, Firewall, then the refresh — the operator's order. */}
          {!omci.isLoading && (bridged || routed) && (
            <Pill tone="neutral">{bridged ? 'Bridged' : 'Routed'}</Pill>
          )}
          {!omci.isLoading && ipv6Pill && (
            <Pill state tone={ipv6Pill.tone} title={ipv6Pill.title}>
              {ipv6Pill.label}
            </Pill>
          )}
          {!omci.isLoading && firewalled !== null && (
            <Pill state tone={firewalled ? 'success' : 'warn'}>
              {firewalled ? 'Firewall on' : 'Firewall off'}
            </Pill>
          )}
          {/* Re-read what this card is drawn from: the CPE's WAN, LAN and WiFi
              over TR-069 when it has a device, else the chassis over OMCI. */}
          <RefreshButton
            size="xs"
            label="the deployed settings"
            run={async () => {
              if (deviceId) {
                for (const scope of ['wan', 'lan', 'wifi'] as const) {
                  await api.post(`/devices/${encodeURIComponent(deviceId)}/refresh`, { scope });
                }
                return { message: 'Re-read the WAN, LAN and WiFi from the ONU.' };
              }
              await api.get(
                `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-config?refresh=1`,
              );
              return { message: 'Re-read the deployed settings from the OLT.' };
            }}
            invalidate={
              deviceId
                ? [['device', deviceId], ['onu', onuId, 'omci-config']]
                : [['onu', onuId, 'omci-config'], ['onu', onuId]]
            }
          />
        </div>
      }
    >
      {omci.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <dl className="divide-y divide-line">
          {config?.unread && (
            <p className="px-4 py-1 text-[11px] text-faint">
              Not read from the OLT yet — the ACS-side view is shown below and the chassis fills in
              shortly.
            </p>
          )}
          <Row icon={<Globe className="size-4" />} label="WAN">
            {wanIp || wanVlan != null ? (
              /*
                A BRIDGED subscriber has no WAN address and no mode, because the
                ONU does not terminate the connection — it hands the VLAN
                straight out and the router behind it does DHCP or PPPoE. Saying
                "no address" there described a fault; the VLAN and the word
                bridged describe the arrangement.

                But only where the chassis SAYS so. `no address` is also the
                honest answer for a ROUTED ONU whose WAN address only its own
                ACS knows — every subscriber on somebody else's TR-069 is in
                exactly that position, and calling them bridged described an
                arrangement they have not got.
              */
              <Detail
                main={bridged ? null : (wanIp ?? (routed ? 'address not reported' : 'no address'))}
                /*
                  The VLAN and the mode, and nothing else (operator, 2026-09-14:
                  "the onu card is too busy … reduce to: 10.2.1.4 · VLAN 5 ·
                  DHCP"). The firewall tag went — the title bar already says
                  "Firewall on|off", and saying it twice on one card was the
                  busyness — and the IPv6 words and the link-local line under
                  the row went with it, to one "IPv6" pill in the title bar
                  whose tooltip carries them (`ipv6TitlePill`).
                */
                tags={[
                  wanVlan != null ? `VLAN ${wanVlan}` : null,
                  /*
                    `bridged` REPLACES the mode rather than joining it: the mode
                    the ACS reports for a bridged unit is its management
                    connection's, and printing both would state a contradiction.

                    `routed` is shown only when there is no mode to show — DHCP
                    or PPPoE already says the ONU terminates the connection, and
                    "routed · DHCP" says it twice. Where neither is known the
                    tag is absent, because the chassis was not asked a question
                    it answers.
                  */
                  bridged ? 'bridged' : (wanMode ?? (routed ? 'routed' : null)),
                ]}
              />
            ) : (
              <Muted>
                {chassisCarriesOnlyManagement
                  ? 'The OLT carries only the management connection for this ONU. The subscriber WAN is set on the ONU itself and shows here once it informs the ACS.'
                  : deviceId
                    ? 'No subscriber connection yet — only management.'
                    : 'Not known from the OLT. Put the ONU on TR-069 to see it.'}
              </Muted>
            )}
          </Row>

          <Row icon={<ShieldCheck className="size-4" />} label="Management">
            {mgmtIp || mgmtVlan != null ? (
              <Detail
                main={mgmtIp ?? '—'}
                tags={[
                  mgmtVlan != null ? `VLAN ${mgmtVlan}` : null,
                  mgmtHost ? (mgmtHost.dhcp ? 'DHCP' : 'static') : null,
                  mgmtHost?.pingResponse === null || mgmtHost?.pingResponse === undefined
                    ? null
                    : mgmtHost.pingResponse
                      ? 'no firewall'
                      : 'firewalled',
                ]}
              />
            ) : (
              <Muted>No management address — this platform cannot reach it.</Muted>
            )}
          </Row>

          <Row icon={<Network className="size-4" />} label="LAN / DHCP">
            {lanIp || lanDhcp !== null ? (
              <Detail
                main={lanIp ?? '—'}
                tags={[
                  lanDhcp === null ? null : lanDhcp ? 'DHCP on' : 'DHCP off',
                  lanDns.length > 0 ? `DNS ${lanDns.join(', ')}` : null,
                ]}
              />
            ) : (
              <Muted>
                {huawei
                  ? 'A Huawei OLT does not report this — it is a TR-069 question.'
                  : routed
                    ? 'Served by the ONU — the chassis does not report the details. Put the ONU on TR-069 to see them.'
                    : bridged
                      ? 'The ONU serves no LAN of its own — the router behind it does.'
                      : 'Not reported by the chassis.'}
              </Muted>
            )}
          </Row>

          <Row icon={<Wifi className="size-4" />} label="WiFi">
            {ssids.length > 0 ? (
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                {ssids.map((s, i) => (
                  <Pill key={`${s.name}-${i}`} tone="neutral">
                    {s.name}
                    {s.band ? ` · ${s.band}` : ''}
                  </Pill>
                ))}
              </div>
            ) : (
              <Muted>
                {huawei
                  ? 'Huawei keeps SSIDs on the ACS — put the ONU on TR-069 to see them.'
                  : omciRadio && omciSlots.length > 0
                    ? `Radio ${omciRadio.enabled === false ? 'off' : 'on'} · ${omciSlots.length} SSID slot${
                        omciSlots.length === 1 ? '' : 's'
                      }${
                        omciWifiUsers > 0
                          ? ` · ${omciWifiUsers} client${omciWifiUsers === 1 ? '' : 's'} connected`
                          : ''
                      } — names set on the router are not reported over OMCI.`
                    : 'No SSID is up.'}
              </Muted>
            )}
          </Row>
        </dl>
      )}
      {/* This card reads; the Configs tab writes. Rather than duplicate every
          editor here, each row of the summary links to the one that changes it —
          "the setting you want", one click away. Each link shows only where a
          real editor waits on the other end: WAN always; WiFi where the CPE (TR-069)
          or a ZTE chassis radio can be edited; LAN only on TR-069 (OMCI has no LAN
          editor, so the link would be dead); Security wherever the firewall is
          controllable — over TR-069 on either vendor, or the ZTE OMCI verb — which
          is everything except a Huawei ONU that has never informed this ACS. */}
      {onEdit &&
        (() => {
          const onTr069 = Boolean(deviceId);
          const links: Array<['wan' | 'lan' | 'wifi' | 'security', string]> = [
            ['wan', 'WAN'],
            ...((onTr069 || (vendor === 'zte' && config?.radio != null)
              ? [['wifi', 'WiFi']]
              : []) as Array<['wifi', string]>),
            ...((onTr069 ? [['lan', 'LAN']] : []) as Array<['lan', string]>),
            ...((onTr069 || vendor === 'zte'
              ? [['security', 'Security']]
              : []) as Array<['security', string]>),
          ];
          return (
            <div className="flex min-h-8 flex-wrap items-center gap-2 border-t border-line px-5 py-1">
              <span className="mr-1 text-xs text-faint">Configure</span>
              {links.map(([focus, label]) => (
                <button
                  key={focus}
                  onClick={() => onEdit(focus)}
                  className="rounded-md border border-primary/30 bg-primary-soft px-2.5 py-0.5 text-xs font-medium text-primary transition hover:border-primary/60 hover:bg-primary/10"
                >
                  {label}
                </button>
              ))}
            </div>
          );
        })()}
    </Card>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-4 px-5 py-1">
      <dt className="flex items-center gap-2 whitespace-nowrap text-sm text-muted">
        {icon}
        {label}
      </dt>
      <dd className="min-w-0 text-right">{children}</dd>
    </div>
  );
}

/** The value that matters, with the rest of the answer beside it. */
function Detail({
  main,
  tags,
}: {
  /** Null where the row has no single headline value — a bridged WAN has none. */
  main: string | null;
  tags: Array<string | null | undefined>;
}) {
  const shown = tags.filter((t): t is string => Boolean(t));
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {main !== null && <span className="font-mono text-sm text-ink">{main}</span>}
      {shown.map((t) => (
        <Pill key={t} tone="neutral">
          {t}
        </Pill>
      ))}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-faint">{children}</span>;
}
